/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * "BE - Immagine - Testo" (sections/beconcept-immaginetesto.liquid)
 *
 * This section reuses the "image-with-text" markup/CSS conventions but is rendered with a plain <div>
 * instead of the theme's <image-with-text> custom element, so none of the reveal-on-scroll behavior that
 * "image-with-text"/"image-with-text-item" provide out of the box (theme.js) applies automatically. The
 * block below replicates just the part of that behavior this section needs: revealing the image and the
 * heading/text once the section scrolls into view.
 */
(function () {
  var sections = document.querySelectorAll(".immagine-testo");
  if (!sections.length) return;

  var prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var canAnimate = prefersReducedMotion !== true && typeof Element !== "undefined" && typeof Element.prototype.animate === "function";

  async function resolveSplitLines(splitLinesElements) {
    var lines = [];
    for (var i = 0; i < splitLinesElements.length; i++) {
      var element = splitLinesElements[i];
      if (typeof element.split === "function") {
        var elementLines = await element.split();
        lines = lines.concat(Array.prototype.slice.call(elementLines));
      }
    }
    return lines;
  }

  async function revealImmagineTesto(section) {
    var image = section.querySelector(".immagine-testo__image[reveal-visibility]");
    var splitLinesElements = section.querySelectorAll(".immagine-testo__content split-lines");
    var textWrapper = section.querySelector(".immagine-testo__text-wrapper[reveal]");
    var lines = await resolveSplitLines(splitLinesElements);

    if (image) {
      if (canAnimate) {
        image.animate(
          [
            { visibility: "hidden", clipPath: "inset(0 0 0 100%)" },
            { visibility: "visible", clipPath: "inset(0 0 0 0)" }
          ],
          { duration: 600, easing: "cubic-bezier(1, 0, 0, 1)", fill: "forwards" }
        );
      } else {
        image.style.visibility = "visible";
      }
    }

    lines.forEach(function (line, index) {
      if (canAnimate) {
        line.animate(
          [
            { opacity: 0, transform: "translateY(100%)", clipPath: "inset(0 0 100% 0)" },
            { opacity: 0.2, transform: "translateY(0)", clipPath: "inset(0 0 0 0)", offset: 0.5 },
            { opacity: 1, transform: "translateY(0)", clipPath: "inset(0 0 0 0)" }
          ],
          { duration: 350, delay: 120 * index, easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)", fill: "forwards" }
        );
      } else {
        line.style.opacity = 1;
      }
    });

    if (textWrapper) {
      if (canAnimate) {
        textWrapper.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, fill: "forwards" });
      } else {
        textWrapper.style.opacity = 1;
      }
    }
  }

  var observer = "IntersectionObserver" in window
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            revealImmagineTesto(entry.target);
          });
        },
        { rootMargin: "30px 0px", threshold: 0 }
      )
    : null;

  sections.forEach(function (section) {
    if (!section.hasAttribute("reveal-on-scroll") || !observer) {
      revealImmagineTesto(section);
      return;
    }
    observer.observe(section);
  });
})();

/**
 * "BE - Hero" (sections/beconcept-slideshow.liquid)
 *
 * The lateral tile image sits next to the <slide-show> as a plain <div>, so it never gets the "Show
 * initial transition" entrance that <slide-show-item> provides out of the box (theme.js). This replicates
 * the same per-transition-type entrance animation (sweep/fade/reveal) for that image, keyed off
 * "data-transition-type" (mirrors section.settings.transition_type) once it scrolls into view.
 */
(function () {
  var images = document.querySelectorAll(".beconcept-slideshow__tile-image[reveal-visibility]");
  if (!images.length) return;

  var prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var canAnimate = prefersReducedMotion !== true && typeof Element !== "undefined" && typeof Element.prototype.animate === "function";

  function reveal(image) {
    if (!canAnimate) {
      image.style.visibility = "visible";
      return;
    }
    var transitionType = image.getAttribute("data-transition-type");
    var keyframes, options;
    switch (transitionType) {
      case "fade":
        keyframes = [{ opacity: 0, visibility: "hidden" }, { opacity: 1, visibility: "visible" }];
        options = { duration: 250, easing: "ease-in-out", fill: "forwards" };
        break;
      case "reveal":
        keyframes = [
          { visibility: "hidden", clipPath: "inset(0 0 100% 0)" },
          { visibility: "visible", clipPath: "inset(0 0 0 0)" }
        ];
        options = { duration: 450, delay: 100, easing: "cubic-bezier(0.5, 0.06, 0.01, 0.99)", fill: "forwards" };
        break;
      default:
        keyframes = [
          { visibility: "hidden", clipPath: "inset(0 0 0 100%)" },
          { visibility: "visible", clipPath: "inset(0 0 0 0)" }
        ];
        options = { duration: 500, easing: "cubic-bezier(1, 0, 0, 1)", fill: "forwards" };
    }
    image.animate(keyframes, options);
  }

  var observer = "IntersectionObserver" in window
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            reveal(entry.target);
          });
        },
        { rootMargin: "30px 0px", threshold: 0 }
      )
    : null;

  images.forEach(function (image) {
    if (!observer) {
      reveal(image);
      return;
    }
    observer.observe(image);
  });
})();

/**
 * "PDP - Zoom sulla desktop-gallery" (snippets/product-media.liquid)
 *
 * The classic gallery's zoom (PhotoSwipe, via <product-image-zoom>) always opens on whichever media is
 * currently ".is-selected" among the ".product__media-item" elements of the mobile flickity carousel -
 * that state is normally kept in sync by <flickity-carousel>/<flickity-controls> as the user swipes or
 * taps a thumbnail. The "desktop-gallery" grid is a separate, independent set of elements (its zoom
 * trigger buttons only carry "data-media-id") and never touches that selection, so opening the zoom
 * from a desktop grid image always opened on the flickity carousel's last-selected slide instead of the
 * image that was actually clicked.
 *
 * This syncs the selection before the zoom opens: on click of a desktop-gallery zoom trigger, mark the
 * matching ".product__media-item" as selected so <product-image-zoom> opens on the right image. Runs in
 * the capture phase so it lands before the "toggle-button" behavior (theme.js) flips "aria-expanded" and
 * opens the zoom modal.
 */
(function () {
  document.addEventListener(
    "click",
    function (event) {
      var trigger = event.target.closest('#desktop-gallery button[is="toggle-button"][data-media-id]');
      if (!trigger) return;

      var productMedia = trigger.closest("product-media");
      if (!productMedia) return;

      var mediaId = trigger.getAttribute("data-media-id");

      productMedia.querySelectorAll(".product__media-item[data-media-id]").forEach(function (item) {
        var isSelectedMedia = item.getAttribute("data-media-id") === mediaId;
        item.classList.toggle("is-selected", isSelectedMedia);
        item.classList.toggle("is-initial-selected", isSelectedMedia);
      });
    },
    true
  );
})();

/**
 * "PDP - Video nella zoom overlay" (snippets/product-media.liquid, assets/theme.js)
 *
 * <product-image-zoom>'s _buildItems() (theme.js, stock/vendored - never edited directly here, see
 * CLAUDE.md) only collects ".product__media-item[data-media-type='image']" to build the PhotoSwipe
 * slide list, so a native Shopify video media never appeared in the zoom overlay.
 *
 * PhotoSwipe v4 (assets/photoswipe.js) only sizes/positions/pans an item when it has a truthy "src"
 * (see "_calculateItemSize"/"_setImageSize" - without "src" the item collapses to w=h=0). So a video
 * item is given a real "src" (the video's poster image) and goes through the exact same load/size
 * pipeline as an image slide. Once that poster <img> has finished loading, the actual <video> element
 * (muted/looped/no controls, built up-front) is layered on top of it, copying the pixel width/height
 * PhotoSwipe just computed for the poster - so it inherits the same positioning/pan-zoom behavior as
 * an image slide (the pan/zoom transform is applied to their shared container, moving both together).
 *
 * PhotoSwipe recycles slide DOM: it only keeps 3 "holders" (prev/current/next) and, whenever one gets
 * reassigned to a different index, wipes it with "holder.el.innerHTML = ''" (see "setContent") -
 * throwing away whatever we appended earlier, including our <video>. Worse, once an item has loaded
 * once, PhotoSwipe's re-render for it on a later visit skips the full load pipeline entirely (the
 * "already loaded" fast path in "setContent") and never fires "imageLoadComplete" again - so relying
 * on that event alone meant the video was inserted only on the very first visit and came back as a
 * frozen static poster every time you scrolled back to it. "afterChange" (fired on every slide change,
 * regardless of which path rendered it) plus a plain DOM-presence check (rather than a one-shot flag)
 * is what makes re-insertion happen reliably on every return visit.
 */
(function () {
  var ProductImageZoomElement = window.customElements.get("product-image-zoom");
  if (!ProductImageZoomElement) return;

  var originalBuildItems = ProductImageZoomElement.prototype._buildItems;
  var originalOpenPhotoSwipe = ProductImageZoomElement.prototype._openPhotoSwipe;

  function buildVideoElement(sources) {
    var bestSource = (sources || [])
      .filter(function (source) {
        return source.mime_type === "video/mp4";
      })
      .sort(function (a, b) {
        return (b.width || 0) - (a.width || 0);
      })[0];
    if (!bestSource) return null;

    var video = document.createElement("video");
    video.src = bestSource.url;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    video.controls = false;
    return video;
  }

  function buildVideoItem(item, product) {
    var mediaId = parseInt(item.getAttribute("data-media-id"), 10);
    var matchedMedia = product.media.find(function (media) {
      return media.id === mediaId;
    });
    if (!matchedMedia || !matchedMedia.preview_image) return null;

    var videoElement = buildVideoElement(matchedMedia.sources);
    if (!videoElement) return null;

    var supportedSizes = getSupportedSizes(matchedMedia, [400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000]);
    var desiredWidth = Math.min(
      supportedSizes[supportedSizes.length - 1] || matchedMedia.preview_image.width,
      window.innerWidth
    );
    var posterUrl = getSizedMediaUrl(matchedMedia, `${Math.ceil(desiredWidth)}x`);

    return {
      mediaId: matchedMedia.id,
      selected: item.classList.contains("is-selected"),
      isVideo: true,
      videoElement,
      src: posterUrl,
      msrc: posterUrl,
      originalMedia: matchedMedia,
      alt: matchedMedia.alt || "",
      w: desiredWidth,
      h: Math.round(desiredWidth / matchedMedia.preview_image.aspect_ratio)
    };
  }

  function insertVideoOverlay(item) {
    if (!item || !item.isVideo || !item.videoElement || !item.container) return;
    if (item.container.contains(item.videoElement)) return;

    var posterImage = item.container.querySelector("img.pswp__img:not(.pswp__img--placeholder)");
    if (!posterImage) return;

    item.videoElement.className = "pswp__img pswp__video";
    item.videoElement.style.width = posterImage.style.width;
    item.videoElement.style.height = posterImage.style.height;
    posterImage.style.opacity = "0";

    item.container.appendChild(item.videoElement);
    item.videoElement.play().catch(function () {});
  }

  function syncVideoItems(pswp) {
    pswp.items.forEach(function (item) {
      if (!item.isVideo || !item.videoElement) return;

      var isMounted = item.container && item.container.contains(item.videoElement);
      if (!isMounted) {
        // Its holder was recycled for a different slide (see block comment above) and threw the
        // old <video> away with it - pause the now-detached instance so it doesn't keep decoding
        // in the background, then try to mount a fresh one into whatever container it has now.
        item.videoElement.pause();
        insertVideoOverlay(item);
      }
    });
  }

  ProductImageZoomElement.prototype._buildItems = async function () {
    var imageItems = await originalBuildItems.call(this);

    var videoElements = Array.from(
      this.mediaElement.querySelectorAll('.product__media-item[data-media-type="video"]:not(.is-filtered)')
    );
    if (!videoElements.length) return imageItems;

    var product = await ProductLoader.load(this.getAttribute("product-handle"));
    var videoItems = videoElements
      .map(function (item) {
        return buildVideoItem(item, product);
      })
      .filter(Boolean);

    if (!videoItems.length) return imageItems;

    imageItems.forEach(function (item) {
      item.mediaId = item.originalMedia && item.originalMedia.id;
    });

    var itemsByMediaId = {};
    imageItems.concat(videoItems).forEach(function (item) {
      itemsByMediaId[item.mediaId] = item;
    });

    return Array.from(this.mediaElement.querySelectorAll(".product__media-item:not(.is-filtered)"))
      .map(function (element) {
        return itemsByMediaId[parseInt(element.getAttribute("data-media-id"), 10)];
      })
      .filter(Boolean);
  };

  ProductImageZoomElement.prototype._openPhotoSwipe = async function () {
    await originalOpenPhotoSwipe.call(this);

    // Belt-and-suspenders: also turn off PhotoSwipe's own close-on-Escape at the source (see the
    // "PDP - Disabilita la chiusura della zoom overlay con ESC" block below for the actual removal).
    this.photoSwipeInstance.options.escKey = false;

    var pswp = this.photoSwipeInstance;
    pswp.listen("imageLoadComplete", function (index, item) {
      insertVideoOverlay(item);
    });
    pswp.listen("afterChange", function () {
      syncVideoItems(pswp);
    });
  };
})();

/**
 * "PDP - La zoom overlay deve chiudersi sempre del tutto" (assets/theme.js, assets/photoswipe.js)
 *
 * ".pswp--open" (photoswipe.js) is the only thing that makes the whole overlay "display: block", and
 * <product-image-zoom>'s own focus trap (OpenableElement, theme.js) is what keeps clicks/keyboard
 * input from reaching the rest of the product page while it's active. Both are only released by
 * theme.js's own "close" listener (`this.open = false`, which removeAttribute()s "open" and, via
 * attributeChangedCallback, deactivates the focus trap) once PhotoSwipe's internal close()/teardown
 * finishes running. That teardown is a long, synchronous chain of calls inside vendored code
 * (assets/photoswipe.js) shared across every listener registered on the "close" event via
 * "_shout" - it has no try/catch, so if ANY of it throws for any reason, the whole chain stops right
 * there: "open" and "pswp--open" are never cleared, and the overlay is stuck blocking the product page
 * underneath with no visible way left to close it.
 *
 * Rather than depend on that internal chain completing (or even starting) cleanly, the close button is
 * given its own, fully independent cleanup: a capture-phase click listener straight on the DOM, which
 * force-clears "open"/"pswp--open" shortly afterwards regardless of what PhotoSwipe's own teardown
 * does or doesn't manage to finish.
 */
(function () {
  function forceCloseZoom(element) {
    element.open = false;
    element.classList.remove("pswp--open", "pswp--animated-in", "pswp--visible", "pswp--animate_opacity");
    element.removeAttribute("style");
    element.setAttribute("aria-hidden", "true");
  }

  document.addEventListener(
    "click",
    function (event) {
      var closeButton = event.target.closest('[data-action="pswp-close"]');
      if (!closeButton) return;

      var zoomElement = closeButton.closest("product-image-zoom");
      if (!zoomElement) return;

      setTimeout(function () {
        forceCloseZoom(zoomElement);
      }, 600);
    },
    true
  );
})();

/**
 * "PDP - Disabilita la chiusura della zoom overlay con ESC" (snippets/product-media.liquid)
 *
 * Pressing Escape closed the zoom overlay through two independent, uncoordinated paths at once:
 * PhotoSwipe's own internal "escKey" handling (assets/photoswipe.js, "_onKeyDown") AND the focus trap
 * that <product-image-zoom> inherits from OpenableElement (assets/theme.js), whose "escapeDeactivates"
 * defaults to true and immediately sets `open = false` on its own, bypassing PhotoSwipe's close()
 * entirely. The two racing "closes" are what left the zoom's backdrop/UI stuck on screen, on top of
 * the product content, instead of actually closing.
 *
 * This removes Escape as a close trigger for the zoom overlay altogether: while it's open, an Escape
 * keydown is stopped in the capture phase, before it can reach either of those two handlers. Closing
 * now only ever happens through PhotoSwipe's own close button (or its swipe-down-to-close gesture),
 * which runs its normal close()/teardown and always fully clears the overlay.
 */
(function () {
  document.addEventListener(
    "keydown",
    function (event) {
      if (event.key !== "Escape" && event.key !== "Esc" && event.keyCode !== 27) return;
      if (!document.querySelector("product-image-zoom[open]")) return;

      event.stopPropagation();
    },
    true
  );
})();